/**
 * EmployeeDashboardPage
 * Dashboard with statistics, charts and quick actions
 */

import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  People,
  PersonAdd,
  FileDownload,
  CalendarToday
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEmployees, useEmployeeStats } from '../../hooks';
import { PageShell } from '../../components';
import { EmployeeStatsCards } from '../../components/employees/EmployeeStatsCards';
import { EmployeeAvatar } from '../../components/employees/EmployeeAvatar';
import { EmployeeStatusChip } from '../../components/employees/EmployeeStatusChip';
import { calculateAge } from '../../utils/employeeFormatters';

export const EmployeeDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  
  const { stats, loading: statsLoading, refresh: refreshStats } = useEmployeeStats();
  const { 
    employees: recentEmployees, 
    loading: recentLoading 
  } = useEmployees({ 
    page: 1, 
    limit: 5, 
    sortBy: 'createdAt', 
    sortOrder: 'DESC' 
  });

  const {
    employees: birthdaysThisMonth,
    loading: birthdaysLoading
  } = useEmployees({
    page: 1,
    limit: 10,
    sortBy: 'birthDate',
    sortOrder: 'ASC'
  });

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Funcionários', path: '/employees' },
        { label: 'Dashboard' },
      ]}
      title="Dashboard - Funcionários"
      subtitle="Visão geral e estatísticas do quadro de funcionários"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => navigate('/employees?export=true')}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/employees/new')}
          >
            Novo Funcionário
          </Button>
        </>
      }
    >
      {/* Stats Cards */}
      <Box mb={4}>
        <EmployeeStatsCards stats={stats} loading={statsLoading} />
      </Box>

      <Grid container spacing={3}>
        {/* Recent Employees */}
        <Grid
          size={{
            xs: 12,
            lg: 6
          }}>
          <Card>
            <CardHeader
              title="Funcionários Recentes"
              subheader="Últimos cadastros realizados"
              avatar={<TrendingUp />}
              action={
                <IconButton onClick={() => navigate('/employees')}>
                  <Refresh />
                </IconButton>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {recentLoading ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Carregando...
                  </Typography>
                </Box>
              ) : recentEmployees.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum funcionário cadastrado recentemente
                  </Typography>
                </Box>
              ) : (
                <List>
                  {recentEmployees.map((employee) => (
                    <ListItem key={employee.id} disablePadding>
                      <ListItemButton onClick={() => navigate(`/employees/${employee.id}`)}>
                        <ListItemAvatar>
                          <EmployeeAvatar
                            name={employee.name}
                            avatarUrl={null}
                            size="small"
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={employee.name}
                          secondary={
                            <>
                              {employee.company && (
                                <>{employee.company.tradeName || employee.company.legalName}</>
                              )}
                              {employee.occupation && ` • ${employee.occupation.name}`}
                            </>
                          }
                        />
                        <EmployeeStatusChip status={employee.status} size="small" />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Birthdays This Month */}
        <Grid
          size={{
            xs: 12,
            lg: 6
          }}>
          <Card>
            <CardHeader
              title="Aniversariantes do Mês"
              subheader={`${new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`}
              avatar={<CalendarToday />}
              action={
                <IconButton onClick={() => navigate('/employees')}>
                  <Refresh />
                </IconButton>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {birthdaysLoading ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Carregando...
                  </Typography>
                </Box>
              ) : birthdaysThisMonth.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum aniversariante este mês
                  </Typography>
                </Box>
              ) : (
                <List>
                  {birthdaysThisMonth.slice(0, 10).map((employee) => {
                    const birthDay = new Date(employee.birthDate).getDate();
                    const age = calculateAge(employee.birthDate);
                    
                    return (
                      <ListItem key={employee.id} disablePadding>
                        <ListItemButton onClick={() => navigate(`/employees/${employee.id}`)}>
                          <ListItemAvatar>
                            <EmployeeAvatar
                              name={employee.name}
                              avatarUrl={null}
                              size="small"
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={employee.name}
                            secondary={`${age} anos`}
                          />
                          <Chip 
                            label={`Dia ${birthDay}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 3
          }}>
          <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
            <People sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Ver Todos
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Acesse a lista completa de funcionários
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/employees')}
            >
              Acessar
            </Button>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 3
          }}>
          <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
            <PersonAdd sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Cadastrar
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Adicione um novo funcionário
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/employees/new')}
            >
              Cadastrar
            </Button>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 3
          }}>
          <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
            <FileDownload sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Exportar
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Baixe relatórios em Excel
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/employees?export=true')}
            >
              Exportar
            </Button>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 3
          }}>
          <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
            <TrendingUp sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Relatórios
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Visualize estatísticas detalhadas
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={refreshStats}
            >
              Atualizar
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </PageShell>
  );
};
